import { useEffect, useState } from "react";
import WarehouseService from "../../services/WarehouseService";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";

// Les dépôts et les produits viennent du système. La lecture RFID est simulée
// par logiciel ; la configuration et les associations d'étiquettes sont
// conservées dans le navigateur (localStorage) en attendant le backend RFID.

const CONFIG_KEY = "rfid.config";
const ASSOCIATIONS_KEY = "rfid.associations";

export const DEFAULT_CONFIG = {
  readerType: "portable-hid",
  readerName: "",
  host: "",
  port: "",
  power: 27,
  readIntervalMs: 200,
  epcFormat: "sgtin96",
  autoStopSeconds: 0,
};

const loadJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};

const saveJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // stockage indisponible : on ignore
  }
};

export const getRfidConfig = () => ({
  ...DEFAULT_CONFIG,
  ...loadJson(CONFIG_KEY, {}),
});
export const saveRfidConfig = (config) => saveJson(CONFIG_KEY, config);

export const getAssociations = () => loadJson(ASSOCIATIONS_KEY, []);
export const saveAssociations = (list) => saveJson(ASSOCIATIONS_KEY, list);

export const randomHex = (length) =>
  Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16))
    .join("")
    .toUpperCase();

// EPC au format SGTIN-96 (en-tête 30h) ; les 22 autres caractères sont aléatoires
export const generateEpc = (format = "sgtin96") =>
  format === "sgtin96" ? "30" + randomHex(22) : randomHex(24);

export const READER_TYPES = [
  { value: "portable-hid", label: "Lecteur portable (mode clavier)" },
  { value: "android-sdk", label: "Lecteur Android (SDK)" },
  { value: "fixed-network", label: "Lecteur fixe / portique (réseau)" },
];

export const fetchWarehouses = async () => {
  const response = await WarehouseService.getAllWarehouse(getCurrentUserId());
  const list = (response.data && response.data.success) || [];
  return list.map((w) => ({
    id: w.id,
    label: (w.ref ? w.ref + " - " : "") + w.name,
  }));
};

export const fetchWarehouseProducts = async (idWarehouse) => {
  const response = await WarehouseService.getAllProductsWarehouseById(
    idWarehouse,
    { page: 0, size: 50 }
  );
  const data = response.data || {};
  const items = (data.products || [])
    .filter((e) => e.product)
    .map((e) => ({
      id: e.id,
      code: e.product.productCode,
      barcode: e.product.barcode,
      name: e.product.name,
      quantity: Number(e.quantity) || 0,
    }));
  return { items, total: data.totalItems || items.length };
};

export const useWarehouses = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchWarehouses()
      .then((list) => !cancelled && setWarehouses(list))
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return { warehouses, loading, error };
};

export const useWarehouseProducts = (idWarehouse) => {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setProducts([]);
    setTotal(0);
    setError(false);
    if (!idWarehouse) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchWarehouseProducts(idWarehouse)
      .then(({ items, total: t }) => {
        if (cancelled) return;
        setProducts(items);
        setTotal(t);
      })
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [idWarehouse]);

  return { products, total, loading, error };
};

export const labelStyle = {
  fontSize: "0.8rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#6c757d",
};
export const cardStyle = {
  borderRadius: 8,
  boxShadow: "0 1px 6px rgba(0,0,0,.08)",
};
